/** The member identity is never stored in the staff token/userInfo slots. */
export const memberSessionKey = "memberSession.v1";
export const memberSessionChangedEvent = "memberAuthStateChanged";

export type MemberSession = {
  accessToken: string;
  refreshToken: string;
  accountId: string;
  sessionId?: string;
  sessionOrder?: string;
  sessionOrigin?: "LOGIN" | "LEGACY_BOOTSTRAP";
};

const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const object = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

export function parseMemberSession(value: unknown): MemberSession {
  if (
    !object(value) ||
    typeof value.accessToken !== "string" ||
    !value.accessToken.trim() ||
    typeof value.refreshToken !== "string" ||
    !value.refreshToken.trim() ||
    typeof value.accountId !== "string" ||
    !uuid.test(value.accountId)
  )
    throw new Error("会員のログイン情報を確認できません");
  const metadata = [value.sessionId, value.sessionOrder, value.sessionOrigin];
  if (
    metadata.some((item) => item !== undefined) &&
    !(
      typeof value.sessionId === "string" &&
      uuid.test(value.sessionId) &&
      typeof value.sessionOrder === "string" &&
      /^[1-9][0-9]*$/.test(value.sessionOrder) &&
      (value.sessionOrigin === "LOGIN" ||
        value.sessionOrigin === "LEGACY_BOOTSTRAP")
    )
  ) {
    throw new Error("会員セッションの識別情報を確認できません");
  }
  return {
    accessToken: value.accessToken,
    refreshToken: value.refreshToken,
    accountId: value.accountId,
    ...(value.sessionId !== undefined
      ? {
          sessionId: value.sessionId as string,
          sessionOrder: value.sessionOrder as string,
          sessionOrigin: value.sessionOrigin as MemberSession["sessionOrigin"],
        }
      : {}),
  };
}

function storage() {
  return typeof window === "undefined" ? null : window.sessionStorage;
}
function read() {
  const raw = storage()?.getItem(memberSessionKey);
  if (!raw) return { revision: null, session: null };
  try {
    const value: unknown = JSON.parse(raw);
    if (
      !object(value) ||
      value.version !== 1 ||
      typeof value.revision !== "string"
    )
      throw new Error();
    return {
      revision: value.revision,
      session:
        value.session === null ? null : parseMemberSession(value.session),
    };
  } catch {
    // A damaged/newer record is not permission to resurrect old shared credentials.
    return { revision: "invalid", session: null };
  }
}

function write(
  session: MemberSession | null,
  revision: string,
  notify: boolean
) {
  const target = storage();
  if (!target) throw new Error("ログイン情報を保存できません");
  target.setItem(
    memberSessionKey,
    JSON.stringify({ version: 1, revision, session })
  );
  if (notify) window.dispatchEvent(new Event(memberSessionChangedEvent));
}

export const memberSessionStorage = {
  read,
  save(value: unknown, expectedRevision: string | null): boolean {
    const session = parseMemberSession(value);
    if (read().revision !== expectedRevision) return false;
    write(session, crypto.randomUUID(), true);
    return true;
  },
  refresh(value: unknown, expected: MemberSession, revision: string): boolean {
    const next = parseMemberSession(value);
    if (
      next.accountId !== expected.accountId ||
      (expected.sessionId !== undefined &&
        (next.sessionId !== expected.sessionId ||
          next.sessionOrder !== expected.sessionOrder ||
          next.sessionOrigin !== expected.sessionOrigin))
    ) {
      throw new Error("ログイン中の会員セッションが変更されました");
    }
    const current = read();
    if (
      current.revision !== revision ||
      current.session?.refreshToken !== expected.refreshToken
    )
      return false;
    // Rotation is not a new login. In-flight UI work remains bound to the same revision.
    write(next, revision, false);
    return true;
  },
  clear(expectedRevision: string | null): boolean {
    if (read().revision !== expectedRevision) return false;
    write(null, crypto.randomUUID(), true);
    return true;
  },
};

/** Routing hint only. The server must verify the signed token before legacy adoption. */
export function isLegacyMemberToken(token: string | null): boolean {
  if (!token) return false;
  try {
    const payload: unknown = JSON.parse(
      atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/"))
    );
    return (
      object(payload) && payload.typ === "parent" && payload.aud === "PARENT"
    );
  } catch {
    return false;
  }
}
